import { useTranslation } from "react-i18next";
import { DataFileImportFormType, DISPLAYNAME } from "../DataFileImportEditorUtils";
import { useFormContext } from "react-hook-form";
import { Alert, Box, MenuItem, Stack, useTheme } from "@mui/material";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { ItemMapFormType } from "../DataFileImportEditorUtils";
import LoadingZone from "../../../../components/loading-zone/LoadingZone";
import DataFileImportItemList from "../data-file-import-item-list/DataFileImportItemList";
import FormSelect from "../../../../components/forms/form-select/FormSelect";
import _ from "lodash";
import { getFilename } from "../../../../common/utilities/AttachmentHelpers";
import { useAllObjectAttributes } from "../../../../queries/attributes/UseAllObjectAttributes";
import { useGroup } from "../../../../queries/groups/UseGroup";
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import AttributeDataService from "../../../../services/attribute-data-service/AttributeDataService";
import { useEffect, useMemo } from "react";
import { useDataFileImportObjectMap } from "../../../../queries/data-file-import/UseDataFileImportObjectMap";
import { IColumn } from "../../../../services/data-file-import-data-service/Models";
import { IKeyValuePair } from "../../../../common/models/KeyValuePair";

export type ObjectsProps = {
     groupID: number,
};

export default function Objects({
    groupID
}: ObjectsProps) {

    const theme = useTheme();
    const { t } = useTranslation();
    const [userState] = useUserContext();
    const {
        getValues,
        watch,
        //trigger,
        //control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<DataFileImportFormType>();    

    const [
        fileName,
        columns,
        fileHasHeaderRow,
        importObjectType,
        objectMap,
        dateColumn,
        identifierColumn,
        timeColumn,
    ] = watch([
        'fileName',
        'columns',
        'fileHasHeaderRow',
        'importObjectType',
        'objectMap',
        'dateColumn',
        'identifierColumn',
        'timeColumn',
    ]);

    const _fileName = getFilename(fileName);
    const _identiferColumn = columns.find(o => o.name === identifierColumn.name);

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const { data, status } = useQueryNotifications(useDataFileImportObjectMap)({
        groupID,
        fileName: _fileName as string,
        fileHasHeaderRow,
        identifierColumn: _identiferColumn as IColumn,
        importObjectType,
        enabled: identifierColumn.name.length > 0 && _fileName !== undefined
    });

    const {data: attributes } = useQueryNotifications(useAllObjectAttributes)({
        groupID: groupID,
        objectType: importObjectType,
    });


    const attributeList = useMemo(() => {

        const list = _.cloneDeep(attributes ?? []);

        if (list.length) {
            const displayNameAttribute = {...list[0]};

            displayNameAttribute.id = -2;
            displayNameAttribute.attributeName = DISPLAYNAME;
            displayNameAttribute.attributeValue = displayNameAttribute.object.displayText!;
            list.push(displayNameAttribute);
        }

        return list.sort((a, b) => a.attributeName.toLowerCase().localeCompare(b.attributeName.toLowerCase()));
    },[
        attributes
    ]);


    const columnOptions = _.cloneDeep(columns).filter(o => o.name !== dateColumn.name && o.name !== timeColumn?.name);

    const pickList = useMemo(() => {
        return data?.objects.map(o => {
            return {
                Key: o.id,
                Value: o.displayText
            } as IKeyValuePair<number, string>
        }) ?? [];
    }, [data])

    async function onAttributeSelected() {

        const val = getValues('attributeName');

        if (val === DISPLAYNAME) {

            objectMap.forEach((o, ndx) => {

                const newItem = {...o,
                    id: -1,
                    name: ''
                };

                const attributeValue = pickList.find(v => v.Value === o.column);

                if (attributeValue) {

                    newItem.id = attributeValue.Key;
                    newItem.objectType = importObjectType;
                    newItem.name = attributeValue.Value;
                }

                setValue(`objectMap.${ndx}`, newItem);
            });

        } else {

            const attributeUsage = await AttributeDataService.getAttributesUsage(
                group!.organizationID!,
                groupID, 
                val
            );

            const attributeValues = attributeUsage?.flatMap(o => o.objectAttributeValues)

            objectMap.forEach((o, ndx) => {

                const newItem = {...o,
                    id: -1,
                    name: ''
                };

                const attributeValue = attributeValues.find(v => v.value === o.column);

                if (attributeValue) {

                    newItem.id = attributeValue.object.id;
                    newItem.objectType = attributeValue.object.objectType;
                    newItem.name = attributeValue.object.displayText!;
                }

                setValue(`objectMap.${ndx}`, newItem);
            });
        }
    }

    function onIdentifierChanged() {

        //setValue('attributeName', '', {shouldDirty: true});

        setValue('objectMap', 
            [], {
            shouldDirty: true, 
            shouldValidate: true
        });
    }
    useEffect(() => {

        //if (data && (data.objectMap.length !== objectMap.length || objectMap.length === 0)) {
        if (data && objectMap.length === 0) {

            const list = data.objectMap.map(o => {

                return {
                    fieldKey: `om-${o.identifier}`,
                    column: o.identifier,
                    id: o.mappedObject.id,
                    objectType: o.mappedObject.objectType,
                    name: o.mappedObject.displayText,
                } as ItemMapFormType;
            });

            list.forEach(o => {
                const item = objectMap.find(v => v.column === o.column);
                if (item) {
                    o.id = item.id;
                    o.objectType = item.objectType;
                    o.name = item.name;
                    o.guid = item.guid;
                }
            });

            setValue('objectMap', 
                    list, {
                    shouldDirty: true, 
                    shouldValidate: true
            });
            
        }
    }, [
        data,
        objectMap,
        setValue,
        columns,
    ]);

   return (
       <Box sx={{
           display:'flex',
           overflow: "hidden",
           flexDirection: 'column',
           flex: '1 1 auto',
           padding: 1,
           backgroundColor: "custom.background.cardContainer",
       }}>
           <LoadingZone isLoading={status === 'loading'}>

               <Stack
                   direction='row'
                   sx={{
                       display: 'flex',
                       justifyContent: 'space-between'
                   }}>
              
                   <FormSelect
                       name="identifierColumn.name"
                       label={t('dataFileImport.identifierColumnLabel')}
                       sx={{ 
                           width: 1,
                           marginTop: 1,
                           marginBottom: 2,
                           maxWidth: '400px'
                       }}
                       disabled={columnOptions.length === 0}
                       onChange={() => onIdentifierChanged()}
                   >
                       <MenuItem value='' disabled={true}>
                       </MenuItem>
                       {
                           columnOptions.map((o) => 
                               <MenuItem key={`identifier.${o.id}`} value={o.name}>
                                   {o.name}
                               </MenuItem>
                           )
                       }

                   </FormSelect>      

                   {
                   (attributeList?.length ?? 0) > 0 &&
                   <FormSelect
                       name="attributeName"
                       label={t('dataFileImport.matchAttribute')}
                       disabled={identifierColumn.name === ''}
                       sx={{ 
                           width: 1,
                           marginTop: 1,
                           marginBottom: 2,
                           marginRight: 2,
                           maxWidth: '400px'
                       }}
                       onChange={() => onAttributeSelected()}
                   >
                       <MenuItem sx={{height: theme.spacing(4)}} value=''>
                       </MenuItem>
                       {
                           attributeList?.map((o) => 
                               <MenuItem key={`attr.${o.id}`} value={o.attributeName}>
                                   {o.attributeName}
                               </MenuItem>
                           )
                       }

                   </FormSelect>
                   }
                      
               </Stack>

               {
               identifierColumn.name.length > 0 ? 
                   <DataFileImportItemList 
                       name='objectMap' 
                       pickList={pickList} 
                   /> :
                   <Alert severity="info"
                       sx={{
                           marginTop: 1,
                           marginRight: 2
                       }}>
                   {t('dataFileImport.objectMapInstruction')}
                   </Alert>
               }

           </LoadingZone>
       </Box>
   );
}
